import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearch, Link } from '@tanstack/react-router';
import { Check, MailCheck, XCircle, CheckCircle2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';
import { z } from 'zod';

import { SILENT_ERRORS_CONTEXT } from '@shared/api/silent-errors';
import { safeRedirectPath } from '@shared/auth/auth';
import { AuthError } from '@shared/auth/errors';
import { useAuth } from '@shared/auth/use-auth';
import { WorkspaceIcon } from '@shared/components/branding/WorkspaceIcon';
import { Button } from '@shared/components/ui/Button';
import { Checkbox } from '@shared/components/ui/Checkbox';
import { Input } from '@shared/components/ui/Input';
import { Label } from '@shared/components/ui/Label';
import { useWorkspaces, type Workspace } from '@shared/hooks/use-workspaces';
import { isPasswordValid } from '@shared/lib/password-policy';

import { requestPasswordRestore, restorePassword } from './api/auth-api';
import { AuthLayout } from './components/AuthLayout';
import { LanguageSwitcher } from './components/LanguageSwitcher';
import { PasswordChecklist } from './components/PasswordChecklist';
import { PasswordInput } from './components/PasswordInput';
import { CREATE_TRADE_SYSTEM_HANDOFF } from './graphql/mutations';

// ── Shared title row (heading + inline language switcher) ───────────────────────

function AuthHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{title}</h1>
        <LanguageSwitcher />
      </div>
      {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
    </div>
  );
}

// ── Login ─────────────────────────────────────────────────────────────────────

const EMAIL_REQUIRED = 'required';
const EMAIL_INVALID = 'invalidEmail';

// DEV-4200: login is now identified by email, not a free-form username —
// the same `.email()` shape ForgotPasswordPage already validates against.
const loginSchema = z.object({
  email: z.string().min(1, EMAIL_REQUIRED).email(EMAIL_INVALID),
  password: z.string().min(1),
  remember: z.boolean(),
});

type LoginForm = z.infer<typeof loginSchema>;

const AUTH_ERROR_KEY: Record<string, string> = {
  INVALID_CREDENTIALS: 'invalidCredentials',
  SERVER_UNAVAILABLE: 'serverUnavailable',
  TOKEN_NOT_RECEIVED: 'tokenNotReceived',
};

export function LoginPage() {
  const { login } = useAuth();
  const { t } = useTranslation('auth');
  const { t: tErrors } = useTranslation('errors');
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting, isValid },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '', remember: true },
    mode: 'onChange',
  });

  const remember = watch('remember');

  const onSubmit = async (data: LoginForm) => {
    setServerError(null);
    try {
      await login(data.email, data.password, data.remember);
    } catch (err) {
      if (err instanceof AuthError) {
        setServerError(tErrors(AUTH_ERROR_KEY[err.code] ?? 'serverError'));
      } else {
        setServerError(tErrors('loginError'));
      }
    }
  };

  return (
    <AuthLayout>
      <AuthHeader title={t('signIn', 'Sign in')} />

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Label htmlFor="email">{t('email', 'Email')}</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            aria-invalid={!!errors.email}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            {...register('email')}
          />
          {errors.email?.message === EMAIL_INVALID && (
            <p className="text-xs text-destructive">
              {t('invalidEmail', 'Enter a valid email address')}
            </p>
          )}
          {errors.email?.message === EMAIL_REQUIRED && (
            <p className="text-xs text-destructive">{t('required', 'Required field')}</p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="password">{t('password', 'Password')}</Label>
          <PasswordInput
            id="password"
            autoComplete="current-password"
            aria-invalid={!!errors.password}
            {...register('password')}
          />
          {errors.password && (
            <p className="text-xs text-destructive">{t('required', 'Required field')}</p>
          )}
        </div>

        {serverError && (
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {serverError}
          </p>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Checkbox
              id="remember"
              checked={remember}
              onCheckedChange={(checked) => {
                setValue('remember', checked === true);
              }}
            />
            <Label htmlFor="remember" className="cursor-pointer font-medium">
              {t('keepSignedIn', 'Keep me signed in')}
            </Label>
          </div>
          <Link
            to="/forgot-password"
            className="text-sm text-muted-foreground underline hover:text-foreground"
          >
            {t('forgotPassword', 'Forgot password?')}
          </Link>
        </div>

        <Button type="submit" className="w-full" disabled={isSubmitting || !isValid}>
          {isSubmitting ? t('signingIn', 'Signing in...') : t('signIn', 'Sign in')}
        </Button>
      </form>
    </AuthLayout>
  );
}

// ── Choose Company ───────────────────────────────────────────────────────────────

/**
 * DEV-4200: shown right after sign-in when the user has more than one
 * available company — `AuthProvider.login()` routes here instead of the
 * final destination once it sees `allowedAccounts.length > 1`. Reuses the
 * same `useWorkspaces`/`switchAccount` mechanism as the sidebar's
 * `WorkspaceSwitcher` (DEV-4100's impersonation token-swap pattern), so a
 * pick here persists exactly the same way a mid-session switch does — a new
 * JWT scoped to the chosen account, re-fetched then the app reloads on the
 * destination the login carried in `url`.
 */
export function ChooseCompanyPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const search = useSearch({ from: '/choose-company' });
  const { active, workspaces, fetching, switching, switchTo } = useWorkspaces();
  const [error, setError] = useState<string | null>(null);

  const destination = safeRedirectPath(search.url);

  // Nothing to choose (or the workspace list resolved to one account after
  // all) — don't strand the user on an empty screen, just continue in.
  useEffect(() => {
    if (!fetching && workspaces.length <= 1) {
      void navigate({ to: destination, replace: true });
    }
  }, [fetching, workspaces.length, navigate, destination]);

  if (fetching || workspaces.length <= 1) return null;

  const onSelect = (workspace: Workspace) => {
    setError(null);
    if (workspace.accountId === active?.accountId) {
      void navigate({ to: destination, replace: true });
      return;
    }
    void switchTo(workspace.accountId, destination).then((ok) => {
      if (!ok) {
        setError(t('chooseCompany.switchFailed', 'Could not switch company. Please try again.'));
      }
    });
  };

  return (
    <AuthLayout>
      <AuthHeader
        title={t('chooseCompany.title', 'Choose a company')}
        subtitle={t('chooseCompany.subtitle', 'Select which company you want to work in')}
      />

      <div className="flex flex-col gap-2">
        {workspaces.map((workspace) => {
          const isActive = workspace.accountId === active?.accountId;
          return (
            <button
              key={workspace.accountId}
              type="button"
              disabled={switching !== null}
              onClick={() => {
                onSelect(workspace);
              }}
              className="flex items-center gap-3 rounded-md border p-3 text-start transition-colors hover:bg-muted disabled:opacity-60"
            >
              <WorkspaceIcon workspace={workspace} className="size-9" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-foreground">
                  {workspace.name ?? t('chooseCompany.noName', 'Unnamed company')}
                </p>
              </div>
              {switching === workspace.accountId ? (
                <span className="text-xs text-muted-foreground">
                  {t('chooseCompany.switching', 'Switching...')}
                </span>
              ) : (
                isActive && <Check className="size-4 shrink-0 text-primary" aria-hidden />
              )}
            </button>
          );
        })}
      </div>

      {error && (
        <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
      )}
    </AuthLayout>
  );
}

// ── Trade System sign-in ────────────────────────────────────────────────────────

interface TradeSystemHandoffData {
  createTradeSystemHandoff: { redirectUrl: string };
}

/** The host `redirect_uri` names, or null when it is not an absolute URL. */
function tradeSystemHost(redirectUri: string | undefined): string | null {
  if (!redirectUri) return null;
  try {
    return new URL(redirectUri).host || null;
  } catch {
    return null;
  }
}

/**
 * The CRM's sign-in to Trade System. Trade System's login page sends a CRM user
 * to `/sso/trade-system?redirect_uri=<its callback>`; the click mints a
 * single-use hand-off token for the signed-in user (`createTradeSystemHandoff`,
 * sent with the app's access token like every GraphQL call) and the tab
 * continues to the callback, token in its fragment, in place of this page.
 */
export function TradeSystemSsoPage() {
  const { t } = useTranslation('auth');
  const { redirect_uri: redirectUri } = useSearch({ from: '/fullscreen/sso/trade-system' });
  const [, createHandoff] = useMutation<TradeSystemHandoffData, { redirectUri: string }>(
    CREATE_TRADE_SYSTEM_HANDOFF,
  );
  const [continuing, setContinuing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const host = tradeSystemHost(redirectUri);

  const onContinue = (target: string) => {
    setError(null);
    setContinuing(true);
    // The page shows the failure itself, so the global error toasts stay out.
    void createHandoff({ redirectUri: target }, SILENT_ERRORS_CONTEXT).then((result) => {
      const redirectUrl = result.data?.createTradeSystemHandoff.redirectUrl;
      if (redirectUrl) {
        window.location.replace(redirectUrl);
        return;
      }
      setContinuing(false);
      const code = result.error?.graphQLErrors[0]?.extensions['code'];
      if (code === 'BAD_REQUEST') {
        setError(t('tradeSystem.invalidLink', 'This Trade System sign-in link is not valid.'));
      } else if (code === 'FORBIDDEN') {
        setError(
          t('tradeSystem.refused', 'This session cannot be used to sign in to Trade System.'),
        );
      } else {
        setError(t('tradeSystem.failed', 'Could not sign in to Trade System. Please try again.'));
      }
    });
  };

  return (
    <AuthLayout>
      <AuthHeader title={t('tradeSystem.title', 'Sign in to Trade System')} />

      {redirectUri && host ? (
        <Button
          className="h-auto min-h-9 w-full whitespace-normal"
          disabled={continuing}
          onClick={() => {
            onContinue(redirectUri);
          }}
        >
          {t('tradeSystem.continue', 'Continue to Trade System ({{host}})', { host })}
        </Button>
      ) : (
        <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {t('tradeSystem.invalidLink', 'This Trade System sign-in link is not valid.')}
        </p>
      )}

      {error && (
        <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
      )}
    </AuthLayout>
  );
}

// ── Forgot Password (request) ───────────────────────────────────────────────────

const forgotSchema = z.object({
  email: z.string().min(1).email(),
});

type ForgotForm = z.infer<typeof forgotSchema>;

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const { t: tErrors } = useTranslation('errors');
  // The recovery endpoint can't fail-fast on a missing email, so a failure with
  // a server message → the dedicated "account problem" screen (showing the
  // backend's already-localized copy, e.g. "No account found…" or a blocked
  // notice). A transport failure (no message) → an inline "server unavailable".
  const [failureMessage, setFailureMessage] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotForm>({
    resolver: zodResolver(forgotSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = async (data: ForgotForm) => {
    setServerError(null);
    setFailureMessage(null);
    const result = await requestPasswordRestore(data.email);
    if (result.success) {
      void navigate({ to: '/check-email', search: { email: data.email } });
      return;
    }
    if (result.unavailable || !result.message) {
      setServerError(tErrors('serverUnavailable'));
      return;
    }
    setFailureMessage(result.message);
  };

  if (failureMessage) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center gap-4 text-center">
          <XCircle className="size-10 text-destructive" aria-hidden />
          <p className="text-2xl font-semibold tracking-tight text-foreground">{failureMessage}</p>
          <Button
            variant="outline"
            className="w-full"
            onClick={() => {
              setFailureMessage(null);
            }}
          >
            {t('recovery.goBack', 'Go back')}
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <AuthHeader
        title={t('recovery.title', 'Password recovery')}
        subtitle={t('recovery.subtitle', 'Enter the email address used during registration')}
      />

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Label htmlFor="email">{t('email', 'Email')}</Label>
          <Input
            id="email"
            type="text"
            autoComplete="email"
            aria-invalid={!!errors.email}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            {...register('email')}
          />
          {errors.email && (
            <p className="text-xs text-destructive">{t('required', 'Required field')}</p>
          )}
        </div>

        {serverError && (
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {serverError}
          </p>
        )}

        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting
            ? t('recovery.sending', 'Sending...')
            : t('recovery.submit', 'Reset password')}
        </Button>
      </form>

      <div className="text-center">
        <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">
          {t('recovery.goBack', 'Go back')}
        </Link>
      </div>
    </AuthLayout>
  );
}

// ── Check your email ────────────────────────────────────────────────────────────

export function CheckEmailPage() {
  const { t } = useTranslation('auth');
  const { email } = useSearch({ from: '/check-email' });
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const onResend = async () => {
    if (!email) return;
    setResending(true);
    setResent(false);
    await requestPasswordRestore(email);
    setResending(false);
    setResent(true);
  };

  return (
    <AuthLayout>
      <div className="flex flex-col items-center gap-4 text-center">
        <MailCheck className="size-8 text-sky-500" aria-hidden />
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            {t('checkEmail.title', 'Check your email')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              'checkEmail.description',
              "We've sent a password reset link to your email. Click the link to choose a new password.",
            )}
          </p>
        </div>
        {resent ? (
          <p className="text-sm text-emerald-600 dark:text-emerald-500">
            {t('checkEmail.resent', 'Email sent again.')}
          </p>
        ) : (
          <Button variant="ghost" onClick={onResend} disabled={resending || !email}>
            {resending ? t('recovery.sending', 'Sending...') : t('checkEmail.resend', 'Resend')}
          </Button>
        )}
      </div>
    </AuthLayout>
  );
}

// ── Set New Password ────────────────────────────────────────────────────────────

const PASSWORD_TOO_SIMPLE = 'PASSWORD_TOO_SIMPLE';
const PASSWORDS_DO_NOT_MATCH = 'PASSWORDS_DO_NOT_MATCH';

/**
 * Schema factory — the password policy needs the user's email (the "not contain
 * email" rule), which comes from the reset token/search params at render time.
 */
function makeResetSchema(email?: string | null) {
  return z
    .object({
      password: z.string(),
      confirmPassword: z.string(),
    })
    .superRefine((data, ctx) => {
      if (!isPasswordValid(data.password, email)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['password'],
          message: PASSWORD_TOO_SIMPLE,
        });
      }
      if (data.confirmPassword !== data.password) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['confirmPassword'],
          message: PASSWORDS_DO_NOT_MATCH,
        });
      }
    });
}

type ResetForm = { password: string; confirmPassword: string };

export function ResetPasswordPage() {
  const { t } = useTranslation('auth');
  const { t: tErrors } = useTranslation('errors');
  const search = useSearch({ from: '/reset-password' });
  // Capture the one-time reset token once, then scrub it from the address bar
  // so it doesn't linger in browser history / referrer headers. The reset
  // request sends the token in the POST body (auth-api), never the URL.
  const tokenRef = useRef(search.token);
  const token = tokenRef.current;
  const email = search.email || undefined;

  useEffect(() => {
    if (!search.token) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('token');
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`,
    );
  }, [search.token]);

  const [success, setSuccess] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ResetForm>({
    resolver: zodResolver(makeResetSchema(email)),
    defaultValues: { password: '', confirmPassword: '' },
    mode: 'onChange',
  });

  const password = watch('password');

  const onSubmit = async (data: ResetForm) => {
    setServerError(null);
    const result = await restorePassword(data.password, token);
    if (result.success) {
      setSuccess(true);
      return;
    }
    // Show the backend's already-localized failure copy (bad/expired/used token
    // or a policy violation the client checklist didn't catch); fall back to a
    // generic message on a transport failure.
    setServerError(
      result.unavailable || !result.message ? tErrors('serverUnavailable') : result.message,
    );
  };

  // Invalid / missing reset link.
  if (!token) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center gap-4 text-center">
          <XCircle className="size-10 text-destructive" aria-hidden />
          <p className="text-sm text-destructive">
            {t('newPassword.invalidLink', 'This reset link is invalid or has expired.')}
          </p>
          <Button variant="outline" className="w-full" asChild>
            <Link to="/login">{t('updated.backToSignIn', 'Go back to sign in')}</Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  // Success: "Password updated".
  if (success) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center gap-4 text-center">
          <CheckCircle2 className="size-9 text-emerald-600 dark:text-emerald-500" aria-hidden />
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              {t('updated.title', 'Password updated')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t('updated.description', 'Your password has been reset successfully.')}
            </p>
          </div>
          <Button variant="outline" className="w-full" asChild>
            <Link to="/login">{t('updated.backToSignIn', 'Go back to sign in')}</Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <AuthHeader
        title={t('newPassword.title', 'Set new password')}
        subtitle={t('newPassword.subtitle', 'Reset the sign in password for your account')}
      />

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Label htmlFor="password">{t('newPassword.password', 'Password')}</Label>
          <PasswordInput
            id="password"
            autoComplete="new-password"
            aria-invalid={!!errors.password}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            {...register('password')}
          />
        </div>

        <PasswordChecklist password={password} email={email} />

        <div className="flex flex-col gap-2">
          <Label htmlFor="confirmPassword">
            {t('newPassword.confirmPassword', 'Confirm Password')}
          </Label>
          <PasswordInput
            id="confirmPassword"
            autoComplete="new-password"
            aria-invalid={!!errors.confirmPassword}
            {...register('confirmPassword')}
          />
          {errors.confirmPassword?.message === PASSWORDS_DO_NOT_MATCH && (
            <p className="text-xs text-destructive">
              {tErrors('passwordsDoNotMatch', 'Passwords do not match')}
            </p>
          )}
          {errors.password?.message === PASSWORD_TOO_SIMPLE && !errors.confirmPassword && (
            <p className="text-xs text-destructive">
              {tErrors(
                'passwordTooSimple',
                'Password is too simple or contains non-Latin characters',
              )}
            </p>
          )}
        </div>

        {serverError && (
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {serverError}
          </p>
        )}

        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting
            ? t('newPassword.saving', 'Saving...')
            : t('newPassword.submit', 'Continue')}
        </Button>
      </form>
    </AuthLayout>
  );
}
